import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, Circle, Thermometer, AlertTriangle, ShieldCheck } from "lucide-react";

export default function Haccp() {
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [openRun, setOpenRun] = useState(null); // template being executed
  const [runData, setRunData] = useState({ items: {}, signature: "" });

  const load = async () => {
    const [st, tpl, r] = await Promise.all([
      api.get("/checklist-runs/status-today"),
      api.get("/checklist-templates"),
      api.get("/checklist-runs?limit=30"),
    ]);
    setStatus(st.data); setTemplates(tpl.data); setRuns(r.data);
  };
  useEffect(() => { load(); }, []);

  const startRun = (tpl) => {
    setOpenRun(tpl);
    setRunData({ items: Object.fromEntries(tpl.items.map((i) => [i.id, { checked: false, value: "", note: "" }])), signature: "" });
  };

  const submitRun = async (e) => {
    e.preventDefault();
    if (!runData.signature.trim()) { toast.error("Firma digitale obbligatoria"); return; }
    const items = openRun.items.map((i) => ({ id: i.id, ...runData.items[i.id] }));
    try {
      const { data } = await api.post("/checklist-runs", {
        template_id: openRun.id,
        date: new Date().toISOString().slice(0, 10),
        items, signature: runData.signature,
      });
      if (data.anomalies.length) toast.warning(`${data.anomalies.length} anomalie rilevate`);
      else toast.success("Checklist firmata e archiviata");
      setOpenRun(null);
      load();
    } catch { toast.error("Errore salvataggio"); }
  };

  return (
    <div data-testid="haccp-page">
      <PageHeader eyebrow="Sicurezza & Compliance" title="HACCP & Checklist"
        subtitle="Registro digitale con firma per apertura, chiusura, temperature. Sempre pronto ai controlli sanitari." />

      {status && (
        <div className="mb-8">
          <div className="label-eyebrow mb-3">Controlli obbligatori di oggi · {status.date}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {status.checklists.map((c) => {
              const tpl = templates.find((t) => t.id === c.template_id);
              return (
                <div key={c.template_id} className={`border rounded-lg p-5 bg-white ${c.completed ? "border-emerald-300" : "border-[#E2D9C8]"}`} data-testid={`haccp-status-${c.template_id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 ${c.completed ? "text-emerald-700" : "text-neutral-400"}`}>
                      {c.completed ? <CheckCircle2 size={20} strokeWidth={1.5} /> : <Circle size={20} strokeWidth={1.5} />}
                    </div>
                    <div className="flex-1">
                      <div className="font-serif text-lg leading-tight">{c.name}</div>
                      <div className="label-eyebrow mt-1">{c.category}</div>
                      {c.completed ? (
                        <div className="mt-3 text-xs text-neutral-500">
                          Firmato da <span className="font-medium text-neutral-900">{c.signed_by}</span> alle {c.completed_at?.slice(11, 16)}
                          {c.anomalies_count > 0 && (
                            <div className="mt-1 text-red-700 flex items-center gap-1"><AlertTriangle size={12} strokeWidth={1.5} /> {c.anomalies_count} anomalie</div>
                          )}
                        </div>
                      ) : (
                        <Button data-testid={`start-run-${c.template_id}`} size="sm" className="mt-3 bg-neutral-900 text-[#FDFBF7]" onClick={() => tpl && startRun(tpl)}>Esegui ora</Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <h2 className="font-serif text-2xl mb-3 mt-10">Storico controlli</h2>
      <div className="border border-[#E2D9C8] rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-sm zebra min-w-[560px]">
          <thead><tr className="text-left label-eyebrow">
            <th className="p-3">Data</th><th className="p-3">Checklist</th><th className="p-3">Firmato da</th>
            <th className="p-3 text-right">Anomalie</th><th className="p-3">Ora</th>
          </tr></thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-t border-[#E2D9C8]" data-testid={`run-${r.id}`}>
                <td className="p-3">{r.date}</td>
                <td className="p-3">{r.template_name}</td>
                <td className="p-3">{r.signed_by_name}</td>
                <td className={`p-3 text-right ${r.anomalies?.length ? "text-red-700 font-medium" : "text-neutral-400"}`}>{r.anomalies?.length || 0}</td>
                <td className="p-3 text-xs text-neutral-500">{r.completed_at?.slice(11, 16)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!openRun} onOpenChange={(v) => !v && setOpenRun(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif flex items-center gap-2"><ShieldCheck size={18} strokeWidth={1.5} /> {openRun?.name}</DialogTitle></DialogHeader>
          <form onSubmit={submitRun} className="space-y-4">
            {openRun?.items.map((it) => (
              <div key={it.id} className="border border-[#E2D9C8] rounded p-3 bg-[#FDFBF7]" data-testid={`haccp-item-${it.id}`}>
                {it.type === "temperature" ? (
                  <>
                    <Label className="flex items-center gap-2 text-sm"><Thermometer size={14} strokeWidth={1.5} /> {it.text}</Label>
                    <div className="text-xs text-neutral-500 mt-1">Range accettabile: {it.min}° – {it.max}°C</div>
                    <Input data-testid={`temp-${it.id}`} type="number" step="0.1" className="mt-2 max-w-[160px]"
                      value={runData.items[it.id]?.value ?? ""}
                      onChange={(e) => setRunData({ ...runData, items: { ...runData.items, [it.id]: { ...runData.items[it.id], checked: true, value: e.target.value } } })} />
                  </>
                ) : (
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox data-testid={`chk-${it.id}`} checked={!!runData.items[it.id]?.checked}
                      onCheckedChange={(v) => setRunData({ ...runData, items: { ...runData.items, [it.id]: { ...runData.items[it.id], checked: !!v } } })} />
                    <span className="text-sm">{it.text}</span>
                  </label>
                )}
              </div>
            ))}
            <div className="pt-3 border-t border-[#E2D9C8]">
              <Label className="label-eyebrow">Firma digitale (nome e cognome)</Label>
              <Input data-testid="signature" required value={runData.signature}
                onChange={(e) => setRunData({ ...runData, signature: e.target.value })}
                placeholder="Es. Luca Piras" className="mt-2 font-serif italic text-lg" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenRun(null)}>Annulla</Button>
              <Button data-testid="submit-run" type="submit" className="bg-neutral-900 text-[#FDFBF7]">Firma e archivia</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
